import { Component, OnInit } from '@angular/core';
import { initDayOfMonth } from 'ngx-bootstrap/chronos/units/day-of-month';
import { timeout } from 'rxjs';
import { AzureAuthService } from 'src/service/azure-auth.service';
import { ApplicationConfigService } from 'src/service/application-config.service';
import * as Handlebars from 'handlebars';
import { UserService } from 'src/api/services';
import { CssLoaderService } from 'src/service/css-loader.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  title = 'INDICIUM-Cloud';

  constructor(private azureAuthService: AzureAuthService, private app: ApplicationConfigService, private cssLoaderService: CssLoaderService) {}

  ngOnInit() {
    const domainConfigs = {
        "interact.enturadigital.com.au": {
            css: 'assets/domains/entura/entura.css',
            title: "Interact",
            favicon: 'assets/domains/entura/entura.png',
            logoUrl: 'assets/domains/entura/entura.png',
            dark: true,
            background: 'https://blob.enturadigital.com.au/interact/background/background.jpg'
        },
        "test.interact.enturadigital.com.au": {
            css: 'assets/domains/entura/entura.css',
            title: "Interact TEST",
            favicon: 'assets/domains/entura/entura.png',
            logoUrl: 'assets/domains/entura/entura.png',
            dark: true,
            background: 'https://blob.enturadigital.com.au/interact-test/background/background.jpg'
        },
        "test.indicium.cloud": {
            css: 'assets/domains/test/test.css',
            title: "INDICIUM TEST",
            dark: false,
        },
        "cloud.fireforesight.au": {
            css: 'assets/domains/fireforesight/style.css',
            title: "Fire Foresight",
            favicon: 'assets/domains/fireforesight/icon.png',
            background: 'assets/domains/fireforesight/background.png',
            dark: true
        },
        "operations.fireforesight.com": {
            css: 'assets/domains/fireforesight/style.css',
            title: "Fire Foresight",
            favicon: 'assets/domains/fireforesight/icon.png',
            background: 'assets/domains/fireforesight/background.png',
            dark: true
        },
        "default": {
            css: 'assets/domains/default/default.css',
            title: "INDICIUM Cloud",
            favicon: 'favicon.ico',
            logoUrl: '/assets/indicium-full.svg',
            dark: true
        },
    };

    const domain = window.location.hostname;
    const config = domainConfigs[domain] ?? domainConfigs["default"];

    var htmlElm = document.documentElement;
    this.app.rootStyles = htmlElm.appendChild(document.createElement("style"));
    if (config == null || config.dark) {        
        htmlElm.setAttribute("data-bs-theme","dark");
    }
    if (config) {
        if (config.logoUrl){
            this.app.logoUrl = config.logoUrl;
        }
        if (config.css) {
            this.cssLoaderService.loadCss(config.css);
        }
        if (config.title) {
            document.title = config.title;
        }
        if (config.favicon) {
            const existingFavicon = document.querySelector("link[rel='icon']");
            if (existingFavicon) {
                existingFavicon.parentNode.removeChild(existingFavicon);
            }
            const link = document.createElement('link');
            link.rel = 'icon';
            link.href = config.favicon;
            document.head.appendChild(link);
        }
        if (config.background){
            this.app.rootStyles.innerHTML = "html:after {background-image:url(" + config.background + ")}";
        } 
        this.app.applicationName = config.title || this.app.applicationName;
    }
    
    this.azureAuthService.onInit();

    this.configureHandlebars();
  }

  configureHandlebars(){
    // This helper gets attached to Handlebars here and allows for
    // conditional if statements within cloud frontend
        Handlebars.registerHelper('ifCond', function (v1, operator, v2, options) {

            switch (operator) {
                case '==':
                    return (v1 == v2) ? options.fn(this) : options.inverse(this);
                case '===':
                    return (v1 === v2) ? options.fn(this) : options.inverse(this);
                case '!=':
                    return (v1 != v2) ? options.fn(this) : options.inverse(this);
                case '!==':
                    return (v1 !== v2) ? options.fn(this) : options.inverse(this);
                case '<':
                    return (v1 < v2) ? options.fn(this) : options.inverse(this);
                case '<=':
                    return (v1 <= v2) ? options.fn(this) : options.inverse(this);
                case '>':
                    return (v1 > v2) ? options.fn(this) : options.inverse(this);
                case '>=':
                    return (v1 >= v2) ? options.fn(this) : options.inverse(this);
                case '&&':
                    return (v1 && v2) ? options.fn(this) : options.inverse(this);
                case '||':
                    return (v1 || v2) ? options.fn(this) : options.inverse(this);
                default:
                    return options.inverse(this);
            }
        });

        Handlebars.registerHelper('jsonParse', function(jsonString) {
            try {
                return JSON.parse(jsonString);
            } catch (e) {
                console.error("Error parsing JSON string:", e, jsonString);
                return jsonString;
            }
        });

        // Registers a helper named 'relativeDate'.
        // It takes an ISO date string as input and returns a string formatted to show how long ago that date was in seconds, minutes, hours, days etc.
        Handlebars.registerHelper('relativeDate', function(dateString) {
            // Return an empty string if the input is null or undefined to avoid errors.
            if (!dateString) {
                return '';
            }

            try {
                const date = new Date(dateString);
                
                // Check if the date object is valid.
                if (isNaN(date.getTime())) {
                    return dateString;
                }

                const now = new Date();
                const diffMs = now.getTime() - date.getTime();
                const diffSeconds = Math.floor(diffMs / 1000);
                
                // Less than a minute
                if (diffSeconds < 60) {
                    return diffSeconds === 1 ? '1 second ago' : `${diffSeconds} seconds ago`;
                }
                
                // Less than an hour
                const diffMinutes = Math.floor(diffSeconds / 60);
                if (diffMinutes < 60) {
                    return diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`;
                }
                
                // Less than a day
                const diffHours = Math.floor(diffMinutes / 60);
                if (diffHours < 24) {
                    return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
                }
                
                // Less than a month
                const diffDays = Math.floor(diffHours / 24);
                if (diffDays < 30) {
                    return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
                }
                
                // Less than a year
                const diffMonths = Math.floor(diffDays / 30);
                if (diffMonths < 12) {
                    return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`;
                }
                
                // A year or more
                const diffYears = Math.floor(diffMonths / 12);
                return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`;
                
            } catch (error) {
                console.error("Error calculating relative date:", error);
                return dateString;
            }
        });

        // Registers a helper named 'formatDate'.
        // It takes an ISO date string as input.
        Handlebars.registerHelper('formatDate', function(dateString) {
            // Return an empty string if the input is null or undefined to avoid errors.
            if (!dateString) {
                return '';
            }

            try {
                const date = new Date(dateString);

                // Check if the date object is valid.
                // An invalid date returns NaN (Not-a-Number) for getTime().
                if (isNaN(date.getTime())) {
                    // If the date is invalid, return the original string.
                    return dateString;
                }

                // Define the formatting options for a readable date and time.
                // This uses the browser's default locale.
                const options: Intl.DateTimeFormatOptions = {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    timeZoneName: 'short'
                };
                
                // Use the Intl.DateTimeFormat API for robust, locale-aware formatting.
                return new Intl.DateTimeFormat('default', options).format(date);

            } catch (error) {
                console.error("Error formatting date:", error);
                // In case of any unexpected error, return the original string.
                return dateString;
            }
        });

        // --- Custom Handlebars Helper for Date Only ---
        Handlebars.registerHelper('formatDateOnly', function(dateString) {
            if (!dateString) return '';
            try {
                const date = new Date(dateString);
                if (isNaN(date.getTime())) return dateString;

                const options: Intl.DateTimeFormatOptions = {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                };
                return new Intl.DateTimeFormat('default', options).format(date);
            } catch (error) {
                console.error("Error formatting date:", error);
                return dateString;
            }
        });

        // --- Custom Handlebars Helper for Time Only ---
        Handlebars.registerHelper('formatTimeOnly', function(dateString) {
            if (!dateString) return '';
            try {
                const date = new Date(dateString);
                if (isNaN(date.getTime())) return dateString;

                const options: Intl.DateTimeFormatOptions = {
                    hour: 'numeric',
                    minute: '2-digit',
                    timeZoneName: 'short'
                };
                return new Intl.DateTimeFormat('default', options).format(date);
            } catch (error) {
                console.error("Error formatting time:", error);
                return dateString;
            }
        });

        // --- Custom Handlebars Helper for Day of the Week ---
        Handlebars.registerHelper('formatDayOfWeek', function(dateString) {
            if (!dateString) return '';
            try {
                const date = new Date(dateString);
                if (isNaN(date.getTime())) return dateString;

                const options: Intl.DateTimeFormatOptions = {
                    weekday: 'long' // 'short' for 'Thu', 'long' for 'Thursday'
                };
                return new Intl.DateTimeFormat('default', options).format(date);
            } catch (error) {
                console.error("Error formatting day of week:", error);
                return dateString;
            }
        });



        // handlebar lookup to find a value in an object with a nested match
        Handlebars.registerHelper('findByMetaTag', function (list, tagName, tagValue, options) {
            if (list == null) return '';

            // Handle single object case
            if (!Array.isArray(list)) {
                if (list.metaData != null && list.metaData.hasOwnProperty(tagName) && list.metaData[tagName] == tagValue) {
                    return options.fn(list);
                }
                return '';
            }

            // Handle array case
            if (list.length == 0) return '';

            var match = list.find(item => {
                return item.metaData != null && item.metaData.hasOwnProperty(tagName) && item.metaData[tagName] == tagValue;
            });

            return match ? options.fn(match) : '';
        });

        // handlebar lookup to find a value in an object with a nested match
        Handlebars.registerHelper('filterByMetaTag', function (list, tagName, tagValue, options) {
            if (list == null || list.length == 0) return '';

            /*for (let item of list) {
                if (item.metaData != null && item.metaData.hasOwnProperty(tagName) && item.metaData[tagName] == tagValue) {
                    options.fn(item);
                }
            }*/
            var matches = list.filter(item => {
                return item.metaData != null && item.metaData.hasOwnProperty(tagName) && item.metaData[tagName] == tagValue;
            })

            return matches.map(item => options.fn(item)).join('');
        });
    }
}
